import g from '#core/global';
import load from '#core/load';
import util from '#core/util';
import expandable from '#partial/expandable/expandable';


// 'per-request': cheapest for short, well-debounced searches (no session token,
// no Place Details call — the field is filled straight from the suggestion's
// own display text). 'session': generates/reuses a session token across a
// search and closes it with a Place Details (Essentials-tier) call on
// selection, which only pays off once a session regularly exceeds ~12
// Autocomplete requests. See .agent/execplan-address-autocomplete.md.
const BILLING_MODE = 'per-request'; // 'per-request' | 'session'

const MIN_QUERY_LENGTH = 4;
const INPUT_DEBOUNCE_MS = 300;

const AUTOCOMPLETE_URL = 'https://places.googleapis.com/v1/places:autocomplete';
const PLACES_BASE_URL = 'https://places.googleapis.com/v1/';
const INCLUDED_REGION_CODES = ['us'];
const INCLUDED_PRIMARY_TYPES = ['street_address', 'premise', 'subpremise'];

const container_$ = '[data-address-autocomplete]';
const input_$ = `${container_$} input[type="search"]`;
const provider_attr = 'data-address-autocomplete';
const supported_providers = ['google-places'];

let has_autocomplete = false;
let instance_count = 0;

// Per-field state (debounced fetcher, in-flight request, dropdown DOM,
// session token, current suggestions) keyed by the input element itself —
// created lazily on first interaction so this module never has to enumerate
// fields at init and never needs to know about `reinitialize`.
const field_states = new WeakMap();


const module = {

	/**
	 * Init module
	 */
	init() {
		if (load.ran('address-autocomplete')) return false;

		load.content(() => {
			has_autocomplete = assign_elements();
			if (!has_autocomplete) return false;
		});
		load.all(() => {
			has_autocomplete = assign_elements();
			if (!has_autocomplete) return false;
		});
	},

};

export default module;
module.init();


/**
 * Assign elements and event listeners (delegated — matches filter.js /
 * expandable.js, not a per-element-at-init scan, so fields added later by an
 * AJAX swap work automatically with no `reinitialize` hookup needed).
 * @return {boolean}
 */
function assign_elements() {
	if (has_autocomplete) return true;
	if (!document.querySelector(container_$)) return false;

	document.addEventListener(g.input_e, e => {
		const $input = e.target.closest(input_$);
		if (!$input) return;

		const $container = $input.closest(container_$);
		if (!is_supported($container)) return;

		on_input($input, $container);
	});

	// Capture phase, ahead of filter.js's bubble-phase Enter handler
	// (`filter.js`'s `[data-filter-address-remote]` Enter listener always
	// submits on Enter) — when a suggestion is highlighted, Enter must select
	// it instead of submitting the not-yet-updated field. Capture guarantees
	// this runs first regardless of module load/registration order, and
	// `stopPropagation()` then keeps filter.js's bubble-phase listener from
	// also firing on the same keystroke.
	document.addEventListener(g.keydown_e, e => {
		const $input = e.target.closest(input_$);
		if (!$input) return;

		const $container = $input.closest(container_$);
		if (!is_supported($container)) return;

		on_keydown(e, $input, $container);
	}, true);

	// Close on genuine blur (e.g. Tab away) — blur doesn't bubble, so this
	// must also be capture phase. Selecting a suggestion never reaches this:
	// suggestions are chosen via `mousedown` + `preventDefault()`, which stops
	// the browser from blurring the field at all.
	document.addEventListener(g.blur_e, e => {
		const $input = e.target.closest ? e.target.closest(input_$) : null;
		if (!$input) return;

		const state = field_states.get($input);
		close_dropdown(state);
	}, true);

	return true;
}


/**
 * Whether this container opted into a provider this module knows how to
 * drive. Leaves room for a future second provider value without a breaking
 * change to the attribute contract.
 * @param {element} $container
 * @return {boolean}
 */
function is_supported($container) {
	if (!$container) return false;
	return supported_providers.includes($container.getAttribute(provider_attr));
}


/**
 * Lazily create (once) and return this field's state, including its dropdown
 * DOM (built once, reused across searches) and its own independent debounced
 * fetcher.
 * @param {element} $input
 * @param {element} $container
 * @return {object}
 */
function get_state($input, $container) {
	let state = field_states.get($input);
	if (state) return state;

	instance_count += 1;
	const id = `address-autocomplete-${instance_count}`;

	const $panel = build_dropdown($input, id);
	$container.appendChild($panel);

	$input.setAttribute('role', 'combobox');
	$input.setAttribute('aria-autocomplete', 'list');
	$input.setAttribute('aria-expanded', g.false_s);
	$input.setAttribute('aria-controls', id);
	$input.setAttribute('autocomplete', 'off');

	state = {
		id,
		$panel,
		$list: $panel.querySelector('[data-expandable-content]'),
		is_open: false,
		suggestions: [],
		active_index: -1,
		session_token: null,
		abort_controller: null,
	};
	state.debounced_fetch = util.debounce(value => {
		fetch_suggestions($input, state, value);
	}, INPUT_DEBOUNCE_MS);

	field_states.set($input, state);
	return state;
}


/**
 * Build the (initially empty, closed) suggestion dropdown, matching the DOM
 * contract `expandable.js`/`expandable.scss` expect (`[data-expandable]` >
 * `.expandable__outer` > `[data-expandable-content]`) so its existing
 * show/hide animation, outside-click-close, and Escape-close all apply with
 * no extra code here.
 * @param {element} $input
 * @param {string} id
 * @return {element}
 */
function build_dropdown($input, id) {
	const $panel = document.createElement('div');
	$panel.id = id;
	$panel.className = 'address-autocomplete__panel';
	$panel.setAttribute('data-expandable', '');
	$panel.setAttribute('data-is-active', g.false_s);
	$panel.setAttribute('aria-busy', g.false_s);
	$panel.setAttribute('data-expandable-outside-close', g.true_s);
	$panel.setAttribute('data-expandable-escape-close', g.true_s);

	const $outer = document.createElement('div');
	$outer.className = 'expandable__outer';

	const $list = document.createElement('ul');
	$list.className = 'address-autocomplete__list';
	$list.id = `${id}-listbox`;
	$list.setAttribute('role', 'listbox');
	$list.setAttribute('data-expandable-content', '');

	$outer.appendChild($list);
	$panel.appendChild($outer);

	return $panel;
}


/**
 * Handle typed input: below the minimum length, close and do nothing further;
 * otherwise debounce, then fetch.
 * @param {element} $input
 * @param {element} $container
 */
function on_input($input, $container) {
	const state = get_state($input, $container);
	const value = $input.value.trim();

	if (value.length < MIN_QUERY_LENGTH) {
		if (state.abort_controller) state.abort_controller.abort();
		close_dropdown(state);
		return;
	}

	state.debounced_fetch(value);
}


/**
 * Handle Up/Down/Enter/Escape while a field's dropdown may be open.
 * @param {event} e
 * @param {element} $input
 * @param {element} $container
 */
function on_keydown(e, $input, $container) {
	const state = field_states.get($input);
	if (!state || !state.is_open) return;

	if (e.key === 'ArrowDown') {
		e.preventDefault();
		move_active(state, 1);
	} else if (e.key === 'ArrowUp') {
		e.preventDefault();
		move_active(state, -1);
	} else if (e.key === 'Enter') {
		if (state.active_index === -1) return; // let Enter fall through to submit
		e.preventDefault();
		e.stopPropagation();
		select_suggestion($input, state, state.active_index);
	} else if (e.key === 'Escape') {
		// Without this, the browser's native `type="search"` behavior clears
		// the field's value on Escape — Escape should only close the dropdown.
		e.preventDefault();
		close_dropdown(state);
	}
}


/**
 * Move the active (highlighted) option, wrapping at either end.
 * @param {object} state
 * @param {number} delta
 */
function move_active(state, delta) {
	if (!state.suggestions.length) return;

	const count = state.suggestions.length;
	state.active_index = (state.active_index === -1)
		? (delta > 0 ? 0 : count - 1)
		: (state.active_index + delta + count) % count;

	Array.from(state.$list.children).forEach(($option, index) => {
		$option.setAttribute('aria-selected', index === state.active_index ? g.true_s : g.false_s);
		$option.classList.toggle('is-active', index === state.active_index);
	});

	state.$input.setAttribute('aria-activedescendant', `${state.id}-option-${state.active_index}`);
}


/**
 * Call the Autocomplete (New) endpoint and render results.
 * @param {element} $input
 * @param {object} state
 * @param {string} value
 */
async function fetch_suggestions($input, state, value) {
	const current_value = $input.value.trim();
	if (current_value.length < MIN_QUERY_LENGTH || current_value !== value) {
		close_dropdown(state);
		return;
	}

	const api_key = window.fritzScripts && window.fritzScripts.google_maps_key;
	if (!api_key) {
		console.error('[address-autocomplete] Missing window.fritzScripts.google_maps_key');
		return;
	}

	if (state.abort_controller) state.abort_controller.abort();
	state.abort_controller = new AbortController();

	const body = {
		input: value,
		includedRegionCodes: INCLUDED_REGION_CODES,
		includedPrimaryTypes: INCLUDED_PRIMARY_TYPES,
	};

	if (BILLING_MODE === 'session') {
		if (!state.session_token) state.session_token = generate_session_token();
		body.sessionToken = state.session_token;
	}

	let response;
	try {
		response = await fetch(AUTOCOMPLETE_URL, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'X-Goog-Api-Key': api_key,
			},
			body: JSON.stringify(body),
			signal: state.abort_controller.signal,
		});
	} catch (error) {
		if (error.name === 'AbortError') return;
		console.error('[address-autocomplete] Autocomplete request failed:', error);
		close_dropdown(state);
		return;
	}

	if (!response.ok) {
		console.error('[address-autocomplete] Autocomplete request failed:', response.status);
		close_dropdown(state);
		return;
	}

	const data = await response.json().catch(() => null);
	const suggestions = (data && data.suggestions) || [];

	render_suggestions($input, state, suggestions);
}


/**
 * Render suggestions into the field's dropdown, opening/closing it via the
 * shared `expandable` module.
 * @param {element} $input
 * @param {object} state
 * @param {array} suggestions
 */
function render_suggestions($input, state, suggestions) {
	state.$input = $input;
	state.suggestions = suggestions.filter(s => s.placePrediction);
	state.active_index = -1;
	state.$list.innerHTML = '';

	if (!state.suggestions.length) {
		close_dropdown(state);
		return;
	}

	state.suggestions.forEach((suggestion, index) => {
		const prediction = suggestion.placePrediction;
		const $option = document.createElement('li');
		$option.id = `${state.id}-option-${index}`;
		$option.className = 'address-autocomplete__option';
		$option.setAttribute('role', 'option');
		$option.setAttribute('aria-selected', g.false_s);
		$option.textContent = (prediction.text && prediction.text.text) || '';

		// mousedown + preventDefault, not click: field.js already binds a
		// (non-delegated) blur listener to every <input> on the page, and
		// without this, clicking a suggestion blurs the field before the
		// click event fires — the classic combobox bug — dropping the
		// selection and risking a flash of field.js's `show-invalid` styling.
		$option.addEventListener('mousedown', e => {
			e.preventDefault();
			select_suggestion($input, state, index);
		});

		state.$list.appendChild($option);
	});

	open_dropdown($input, state);
}


/**
 * Open the dropdown via the shared `expandable` module.
 * @param {element} $input
 * @param {object} state
 */
function open_dropdown($input, state) {
	state.is_open = true;
	$input.setAttribute('aria-expanded', g.true_s);
	expandable.toggle({ id: state.id, force: true });
}


/**
 * Close the dropdown via the shared `expandable` module.
 * @param {object} state
 */
function close_dropdown(state) {
	if (!state || !state.is_open) return;

	state.is_open = false;
	state.active_index = -1;
	if (state.$input) {
		state.$input.setAttribute('aria-expanded', g.false_s);
		state.$input.setAttribute('aria-activedescendant', '');
	}
	expandable.toggle({ id: state.id, force: false });
}


/**
 * Apply a chosen suggestion to the field and submit the search. In
 * 'per-request' mode the field is filled with the suggestion's own display
 * text; in 'session' mode it's the canonical `formattedAddress` from a Place
 * Details (Essentials) call that also closes the session, falling back to
 * the display text on any failure.
 *
 * Deliberately never dispatches `filter.request_e` (which would trigger
 * `filter.js`'s GET-based `filter_results()`) — submission goes through a
 * synthetic Enter keydown instead, so it runs through the exact same
 * already-POST path a real Enter press does (see `submit_search()`), with no
 * separate/duplicated submit logic living in this module.
 *
 * @param {element} $input
 * @param {object} state
 * @param {number} index
 */
async function select_suggestion($input, state, index) {
	const suggestion = state.suggestions[index];
	if (!suggestion || !suggestion.placePrediction) return;

	const prediction = suggestion.placePrediction;
	const display_text = (prediction.text && prediction.text.text) || '';

	if (BILLING_MODE !== 'session') {
		apply_value($input, display_text);
		close_dropdown(state);
		submit_search($input);
		return;
	}

	close_dropdown(state);

	const api_key = window.fritzScripts && window.fritzScripts.google_maps_key;
	let formatted_address = display_text;

	if (api_key && prediction.place && state.session_token) {
		try {
			const response = await fetch(
				`${PLACES_BASE_URL}${prediction.place}?sessionToken=${encodeURIComponent(state.session_token)}`,
				{
					headers: {
						'X-Goog-Api-Key': api_key,
						'X-Goog-FieldMask': 'formattedAddress',
					},
				}
			);
			if (response.ok) {
				const data = await response.json().catch(() => null);
				if (data && data.formattedAddress) formatted_address = data.formattedAddress;
			} else {
				console.error('[address-autocomplete] Place Details request failed:', response.status);
			}
		} catch (error) {
			console.error('[address-autocomplete] Place Details request failed:', error);
		}
	}

	// Rotate to a fresh token for this field's next search.
	state.session_token = null;

	apply_value($input, formatted_address);
	submit_search($input);
}


/**
 * Submit the field by dispatching a synthetic Enter keydown, so selecting a
 * suggestion runs through `filter.js`'s existing (already-POST) Enter
 * handler for `[data-filter-address-remote]` fields — same submit path a
 * real Enter press uses, nothing duplicated here. Only called after the
 * dropdown is already closed (`state.is_open = false`), so this module's own
 * capture-phase Enter handling (`on_keydown`) — which only acts while a
 * dropdown is open — ignores it and lets it reach `filter.js` untouched.
 * @param {element} $input
 */
function submit_search($input) {
	$input.dispatchEvent(new KeyboardEvent('keydown', {
		key: 'Enter',
		bubbles: true,
		cancelable: true,
	}));
}


/**
 * Set the field's value without dispatching a synthetic `input` event (a
 * real, user-typed `input` event is what drives this module's own fetch
 * cycle — dispatching one here would immediately re-trigger a new,
 * unnecessary search against the value we just set).
 * @param {element} $input
 * @param {string} value
 */
function apply_value($input, value) {
	if (!value) return;

	$input.value = value;
	$input.focus();
}


/**
 * A v4 UUID for Autocomplete/Details session grouping (session mode only).
 * @return {string}
 */
function generate_session_token() {
	if (window.crypto && typeof window.crypto.randomUUID === 'function') {
		return window.crypto.randomUUID();
	}

	// Fallback for older browsers without crypto.randomUUID().
	return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
		const r = (Math.random() * 16) | 0;
		const v = c === 'x' ? r : (r & 0x3) | 0x8;
		return v.toString(16);
	});
}
